import { useState, useEffect, useCallback } from 'react'
import { lumi } from '@/lib/lumi'

export interface SiteContentItem {
  _id?: string
  pageKey: string
  sectionKey: string
  contentType: 'title' | 'subtitle' | 'paragraph' | 'button' | 'list' | 'card'
  value: string
  order?: number
  creator?: string
  createdAt?: string
  updatedAt?: string
}

// In-memory cache shared across components
let contentCache: Record<string, string> = {}
let idCache: Record<string, string> = {}
const listeners = new Set<() => void>()

const notifyListeners = () => {
  listeners.forEach((fn) => fn())
}

export function useSiteContent(pageKey?: string) {
  const [items, setItems] = useState<SiteContentItem[]>([])
  const [loading, setLoading] = useState(false)
  const [, forceUpdate] = useState(0)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const filter = pageKey ? { pageKey } : {}
      const { list } = await lumi.entities.site_content.list({
        filter,
        sort: { order: 1 },
      })
      const typed = (list || []) as SiteContentItem[]
      setItems(typed)
      typed.forEach((item) => {
        contentCache[`${item.pageKey}:${item.sectionKey}`] = item.value
        if (item._id) idCache[`${item.pageKey}:${item.sectionKey}`] = item._id
      })
      notifyListeners()
    } catch (err) {
      console.error('Fetch site content failed:', err)
    } finally {
      setLoading(false)
    }
  }, [pageKey])

  useEffect(() => {
    fetchAll()
    const listener = () => forceUpdate((n) => n + 1)
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }, [fetchAll])

  const getText = useCallback(
    (sectionKey: string, fallback: string) => {
      const key = `${pageKey}:${sectionKey}`
      return contentCache[key] ?? fallback
    },
    [pageKey]
  )

  const upsert = async (item: SiteContentItem) => {
    const now = new Date().toISOString()
    const user = lumi.auth.user
    const creator = user?.userId || 'admin'

    if (item._id) {
      await lumi.entities.site_content.update(item._id, {
        value: item.value,
        contentType: item.contentType,
        order: item.order ?? 0,
        updatedAt: now,
      })
    } else {
      await lumi.entities.site_content.create({
        pageKey: item.pageKey,
        sectionKey: item.sectionKey,
        contentType: item.contentType,
        value: item.value,
        order: item.order ?? 0,
        creator,
        createdAt: now,
        updatedAt: now,
      })
    }
    contentCache[`${item.pageKey}:${item.sectionKey}`] = item.value
    notifyListeners()
    await fetchAll()
  }

  const remove = async (id: string) => {
    await lumi.entities.site_content.delete(id)
    await fetchAll()
  }

  return { items, loading, fetchAll, getText, upsert, remove }
}

export function getCachedText(pageKey: string, sectionKey: string, fallback: string) {
  return contentCache[`${pageKey}:${sectionKey}`] ?? fallback
}

// Preload content on app start
export async function preloadSiteContent() {
  try {
    const { list } = await lumi.entities.site_content.list({ sort: { order: 1 } })
    ;(list || []).forEach((item: SiteContentItem) => {
      contentCache[`${item.pageKey}:${item.sectionKey}`] = item.value
      if (item._id) idCache[`${item.pageKey}:${item.sectionKey}`] = item._id
    })
    notifyListeners()
  } catch (err) {
    console.error('Preload site content failed:', err)
  }
}

// Save a text directly from the front (inline editing), creating or updating
// the underlying site_content document as needed. Updates the shared cache
// immediately and notifies every EditableText instance on the page.
export async function saveInlineText(
  pageKey: string,
  sectionKey: string,
  value: string,
  contentType: SiteContentItem['contentType'] = 'paragraph'
) {
  const key = `${pageKey}:${sectionKey}`
  const now = new Date().toISOString()
  const user = lumi.auth.user
  const creator = user?.userId || 'admin'
  const existingId = idCache[key]

  // Optimistic update so the UI reflects the change instantly
  contentCache[key] = value
  notifyListeners()

  try {
    if (existingId) {
      await lumi.entities.site_content.update(existingId, {
        value,
        contentType,
        updatedAt: now,
      })
    } else {
      const created = await lumi.entities.site_content.create({
        pageKey,
        sectionKey,
        contentType,
        value,
        order: 0,
        creator,
        createdAt: now,
        updatedAt: now,
      })
      const newId = (created as any)?._id || (created as any)?.id
      if (newId) idCache[key] = newId
    }
  } catch (err) {
    console.error('Save inline text failed:', err)
    throw err
  }
}
